import { useState } from 'react';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth/authContext';
import { useProperty } from '@/lib/property/propertyContextDef';
import { HostCancelBooking } from '@/lib/bookings/HostCancelBooking';
import {
  icalFeedStatusLabel,
  isLiveBookingStatus,
  mintIcalExportUrl,
  useAddImportFeed,
  useIcalConflicts,
  useIcalFeeds,
  useResolveIcalConflict,
  useRevokeIcalFeed,
} from '@/lib/data/ical';
import { EdgeFunctionError } from '@/lib/api/edgeFunction';

interface CancelTarget {
  conflictId: string;
  bookingId: string;
  reference: string;
  status: string;
  label: string;
}

/** ends_on is exclusive in the DB; show the last blocked night (one date for a one-night clash). */
function clashRange(startsOn: string, endsOnExclusive: string): string {
  const d = new Date(`${endsOnExclusive}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  const last = d.toISOString().slice(0, 10);
  return last <= startsOn ? startsOn : `${startsOn} → ${last}`;
}

export const Route = createFileRoute('/listings/$id/ical')({
  component: ListingIcalPage,
});

function ListingIcalPage() {
  const { id } = useParams({ from: '/listings/$id/ical' });
  const { supabase } = useAuth();
  const { businessId, role } = useProperty();
  const feedsQuery = useIcalFeeds(supabase, id);
  const conflictsQuery = useIcalConflicts(supabase, id);
  const addImport = useAddImportFeed(supabase);
  const revokeFeed = useRevokeIcalFeed(supabase, id);
  const resolveConflict = useResolveIcalConflict(supabase, id);

  const [importUrl, setImportUrl] = useState('');
  const [importLabel, setImportLabel] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [exportUrl, setExportUrl] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [minting, setMinting] = useState(false);
  // Keep external on a clash with a live NamibiaGo booking does not resolve
  // directly — it opens the host-cancel flow for that booking under the row.
  // The target is snapshotted so the row and its outcome banner stay up after
  // the conflict resolves and the booking turns cancelled, until the owner
  // presses Done.
  const [cancelTarget, setCancelTarget] = useState<CancelTarget | null>(null);
  // Conflict id whose booking HostCancelBooking reported cancelled. When the
  // kept_external resolve that follows fails, the row is still unresolved and
  // the cancel form has no Back of its own — this is what keeps Retry resolve
  // and Done on screen so the panel is never a dead end.
  const [cancelledFor, setCancelledFor] = useState<string | null>(null);

  const exportFeed = (feedsQuery.data ?? []).find((f) => f.direction === 'export' && !f.revoked_at) ?? null;
  const importFeeds = (feedsQuery.data ?? []).filter((f) => f.direction === 'import' && !f.revoked_at);
  const shownConflicts = (conflictsQuery.data ?? []).filter(
    (c) => !c.resolved_at || c.id === cancelTarget?.conflictId,
  );

  async function handleMint() {
    setExportError(null);
    setMinting(true);
    try {
      const res = await mintIcalExportUrl(supabase, id);
      setExportUrl(res.url);
    } catch (err) {
      if (err instanceof EdgeFunctionError && err.code === 'export_url_already_minted') {
        setExportError('An export URL already exists for this listing, but it was only shown once — revoke and add a new one to get a fresh link.');
      } else {
        setExportError(err instanceof Error ? err.message : 'Could not create an export link.');
      }
    } finally {
      setMinting(false);
    }
  }

  return (
    <section className="mx-auto flex max-w-2xl flex-col gap-lg">
      <div className="flex items-center justify-between">
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Calendar sync</h1>
        <Link to="/listings/$id/edit" params={{ id }} className="text-body text-canyon underline">
          Back to listing
        </Link>
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Export to another calendar</h2>
        <p className="mt-xs text-body text-ink-2">
          Get a subscribe link for Google Calendar / Airbnb / Booking.com that shows your NamibiaGo bookings and
          manual closures as busy.
        </p>
        {exportFeed && !exportUrl ? (
          <p className="mt-sm text-body text-ink-2">
            An export link already exists. It's only shown once when created — revoke it below to mint a new one if
            you lost it.
          </p>
        ) : null}
        {!exportFeed ? (
          <button
            type="button"
            onClick={handleMint}
            disabled={minting}
            className="mt-md rounded-button bg-canyon px-md py-xs text-body font-semibold text-white disabled:opacity-50"
          >
            {minting ? 'Creating…' : 'Create export link'}
          </button>
        ) : null}
        {exportUrl ? (
          <p className="mt-sm break-all rounded-input border border-hair bg-surface-2 p-sm text-caption text-ink">{exportUrl}</p>
        ) : null}
        {exportError ? <p className="mt-sm text-body text-danger">{exportError}</p> : null}
        {exportFeed ? (
          <button
            type="button"
            onClick={() => revokeFeed.mutate(exportFeed.id)}
            className="mt-sm text-caption text-danger underline"
          >
            Revoke export link
          </button>
        ) : null}
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Import from another calendar</h2>
        <p className="mt-xs text-body text-ink-2">
          Paste an https:// .ics link from Airbnb or Booking.com. NamibiaGo checks it every 15 minutes.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setImportError(null);
            addImport.mutate(
              { listingId: id, url: importUrl, label: importLabel || undefined },
              {
                onSuccess: () => {
                  setImportUrl('');
                  setImportLabel('');
                },
                onError: (err) => setImportError(err instanceof Error ? err.message : 'Could not add this feed.'),
              },
            );
          }}
          className="mt-md flex flex-wrap items-end gap-sm"
        >
          <label className="flex flex-1 flex-col gap-xs text-caption text-ink-2">
            Feed URL
            <input
              type="url"
              required
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              placeholder="https://…"
              className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
            />
          </label>
          <label className="flex flex-col gap-xs text-caption text-ink-2">
            Label (optional)
            <input
              value={importLabel}
              onChange={(e) => setImportLabel(e.target.value)}
              className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
            />
          </label>
          <button
            type="submit"
            disabled={addImport.isPending}
            className="rounded-button bg-canyon px-md py-xs text-body font-semibold text-white disabled:opacity-50"
          >
            {addImport.isPending ? 'Adding…' : 'Add feed'}
          </button>
        </form>
        {importError ? <p className="mt-sm text-body text-danger">{importError}</p> : null}

        {importFeeds.length > 0 ? (
          <ul className="mt-md flex flex-col gap-sm">
            {importFeeds.map((f) => (
              <li key={f.id} className="flex flex-wrap items-center justify-between gap-sm rounded-input border border-hair px-md py-sm">
                <span className="truncate text-body text-ink">{f.label ?? f.url}</span>
                <span className="text-caption text-ink-2">{icalFeedStatusLabel(f)}</span>
                <button type="button" onClick={() => revokeFeed.mutate(f.id)} className="text-caption text-danger underline">
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-md text-body text-ink-2">No import feeds yet.</p>
        )}
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Conflicts</h2>
        <p className="mt-xs text-body text-ink-2">
          An imported date that overlaps a NamibiaGo booking or a manual closure is never auto-resolved — decide what
          to keep.
        </p>
        {shownConflicts.length === 0 ? (
          <p className="mt-md text-body text-ink-2">No open conflicts.</p>
        ) : (
          <ul className="mt-md flex flex-col gap-sm">
            {shownConflicts.map((c) => {
              const label = c.feed?.label ?? 'Imported calendar';
              const resolved = !!c.resolved_at;
              const liveBooking =
                c.kind === 'booking_overlap' && c.booking && isLiveBookingStatus(c.booking.status) ? c.booking : null;
              return (
                <li key={c.id} className="rounded-input border border-warning bg-tint-warning p-md">
                  <p className="text-body text-ink">
                    {label} · {clashRange(c.starts_on, c.ends_on)}
                  </p>
                  <p className="mt-xs text-caption text-ink-2">
                    {c.kind === 'booking_overlap' ? (
                      c.booking ? (
                        <>
                          Overlaps booking{' '}
                          <Link
                            to="/bookings/$reference"
                            params={{ reference: c.booking.reference_number }}
                            className="font-semibold text-canyon underline"
                          >
                            {c.booking.reference_number}
                          </Link>{' '}
                          ({c.booking.status})
                        </>
                      ) : (
                        'Overlaps a NamibiaGo booking that no longer exists'
                      )
                    ) : (
                      'Overlaps dates you set by hand'
                    )}
                  </p>
                  <div className="mt-sm flex gap-sm">
                    {resolved ? (
                      <p className="text-body text-ink">Resolved — kept {label}.</p>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => resolveConflict.mutate({ conflictId: c.id, resolution: 'kept_namibiago' })}
                          className="rounded-button border border-border bg-surface px-sm py-xs text-caption font-semibold text-ink"
                        >
                          Keep NamibiaGo
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (liveBooking) {
                              setCancelTarget({
                                conflictId: c.id,
                                bookingId: c.booking_id!,
                                reference: liveBooking.reference_number,
                                status: liveBooking.status,
                                label,
                              });
                              return;
                            }
                            resolveConflict.mutate({ conflictId: c.id, resolution: 'kept_external' });
                          }}
                          className="rounded-button border border-border bg-surface px-sm py-xs text-caption font-semibold text-ink"
                        >
                          Keep external
                        </button>
                        <button
                          type="button"
                          onClick={() => resolveConflict.mutate({ conflictId: c.id, resolution: 'dismissed' })}
                          className="rounded-button border border-border bg-surface px-sm py-xs text-caption font-semibold text-ink"
                        >
                          Dismiss
                        </button>
                      </>
                    )}
                  </div>
                  {cancelTarget?.conflictId === c.id ? (
                    <div className="mt-md flex flex-col gap-sm">
                      {cancelTarget.status === 'confirmed' ? (
                        <>
                          <p className="text-body text-ink">
                            Keeping {cancelTarget.label} means cancelling {cancelTarget.reference}: the guest gets a full refund of
                            the booking fee and a strike is recorded on your business.
                          </p>
                          <HostCancelBooking
                            businessId={businessId}
                            bookingId={cancelTarget.bookingId}
                            reference={cancelTarget.reference}
                            status={cancelTarget.status}
                            role={role}
                            defaultReason={`Calendar clash with ${cancelTarget.label}: these dates are already taken there.`}
                            returnTo={`/listings/${id}/ical`}
                            openInitially
                            onBack={() => setCancelTarget(null)}
                            onCancelled={() => {
                              setCancelledFor(cancelTarget.conflictId);
                              resolveConflict.mutate({ conflictId: cancelTarget.conflictId, resolution: 'kept_external' });
                            }}
                          />
                        </>
                      ) : (
                        <p className="text-body text-ink">
                          This is still a request. Decline{' '}
                          <Link
                            to="/bookings/$reference"
                            params={{ reference: cancelTarget.reference }}
                            className="font-semibold text-canyon underline"
                          >
                            {cancelTarget.reference}
                          </Link>{' '}
                          first, then come back to keep {cancelTarget.label}.
                        </p>
                      )}
                      {/* Cancel succeeded but the kept_external resolve did not (the
                          error prints below the list): offer the resolve again. Done
                          leaves the row open so Keep external can be pressed later —
                          the booking is already cancelled, so the DB will accept it. */}
                      {cancelledFor === c.id && !resolved ? (
                        <button
                          type="button"
                          onClick={() => resolveConflict.mutate({ conflictId: c.id, resolution: 'kept_external' })}
                          disabled={resolveConflict.isPending}
                          className="self-start rounded-button border border-border bg-surface px-sm py-xs text-caption font-semibold text-ink disabled:opacity-50"
                        >
                          Retry resolve
                        </button>
                      ) : null}
                      {/* The confirmed, unresolved form already has HostCancelBooking's own
                          Back (wired to onBack above) — one Back per panel. */}
                      {resolved || cancelledFor === c.id || cancelTarget.status !== 'confirmed' ? (
                        <button
                          type="button"
                          onClick={() => {
                            setCancelTarget(null);
                            setCancelledFor(null);
                            resolveConflict.reset();
                          }}
                          className="self-start text-caption text-ink-2 underline"
                        >
                          {resolved || cancelledFor === c.id ? 'Done' : 'Back'}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        {resolveConflict.error ? (
          <p className="mt-sm text-body text-danger">{resolveConflict.error.message}</p>
        ) : null}
      </div>
    </section>
  );
}
